import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReminderHighlight } from 'src/components/atoms/reminder-highlight';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { Booking } from 'src/features/bookings/api/types/response/booking.response';
import { useUnassignSpaceGroupFromEmployeeGroup } from 'src/features/employees-groups/api/hooks/space-groups/use-unassign-space-group-from-employee-group';
import { useDialog } from 'src/hooks/use-dialog';
import { CancelledBookingsSummaryDialog } from './cancelled-bookings-summary-dialog';

interface Props extends DialogActions {
  spaceGroupToken: string;
  bookingsToManage: Booking[];
}

export const EmployeesHaveActiveOrFutureBookingsDialog = ({
  open,
  onCancel,
  spaceGroupToken,
  bookingsToManage,
}: Props) => {
  const { t } = useTranslation(['employeesGroups', 'common']);

  const { employeeGroupToken } = useParams({
    from: '/_layout/tenant/employees/groups/$groupToken/_layout/space-groups',
    select(state) {
      return {
        employeeGroupToken: state.groupToken,
      };
    },
  });

  const [
    cancelAllActiveAndFutureBookings,
    setCancelAllActiveAndFutureBookings,
  ] = useState<boolean>(true);

  const [
    isCancelledBookingsSummaryDialogOpen,
    handleOpenCancelledBookingsSummaryDialog,
    handleCloseCancelledBookingsSummaryDialog,
  ] = useDialog();

  const handleCancel = () => {
    onCancel?.();
    setCancelAllActiveAndFutureBookings(true);
  };

  const {
    mutate: unassignSpaceGroupFromEmployeeGroup,
    isPending: isUnassigningSpaceGroupFromEmployeeGroup,
  } = useUnassignSpaceGroupFromEmployeeGroup(handleCancel);

  const handleChangeDecision = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = JSON.parse(event.target.value);

    if (typeof parsedValue === 'boolean') {
      setCancelAllActiveAndFutureBookings(parsedValue);
    }
  };

  const handleUnassignSpaceGroupFromEmployeeGroup = async () => {
    const bookingDecisions = bookingsToManage.map(({ token }) => ({
      bookingToken: token,
    }));

    unassignSpaceGroupFromEmployeeGroup({
      employeeGroupToken,
      spaceGroupToken,
      bookingsDecisions: {
        moveBookingDecisions: [],
        cancelBookingDecisions: cancelAllActiveAndFutureBookings
          ? bookingDecisions
          : [],
        keepBookingUnchangedDecisions: cancelAllActiveAndFutureBookings
          ? []
          : bookingDecisions,
      },
      bookingsDecisionsReason: t(
        'employeesGroups:spaceGroups.bookingsDecisionsReason',
      ),
    });

    if (cancelAllActiveAndFutureBookings) {
      handleOpenCancelledBookingsSummaryDialog();
    }
  };

  return (
    <>
      <Dialog
        title={t(
          'employeesGroups:spaceGroups.employeesHaveActiveOrFutureBookingsDialog.title',
        )}
        open={open}
        onCancel={handleCancel}
        cancelButtonText={t('common:button.cancel')}
        onConfirm={handleUnassignSpaceGroupFromEmployeeGroup}
        confirmButtonText={t('common:button.confirm')}
        isConfirmButtonSubmitting={isUnassigningSpaceGroupFromEmployeeGroup}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ReminderHighlight>
            <Typography variant="body2">
              {t(
                'employeesGroups:spaceGroups.employeesHaveActiveOrFutureBookingsDialog.cautionMessage',
              )}
            </Typography>
          </ReminderHighlight>

          <FormControl>
            <RadioGroup
              aria-labelledby="bookings-decision-radio-buttons-group"
              name="bookings-decision-radio-buttons-group"
              value={cancelAllActiveAndFutureBookings}
              onChange={handleChangeDecision}>
              <FormControlLabel
                value={true}
                control={<Radio />}
                label={t(
                  'employeesGroups:spaceGroups.employeesHaveActiveOrFutureBookingsDialog.cancel.title',
                )}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mb: 2 }}>
                {t(
                  'employeesGroups:spaceGroups.employeesHaveActiveOrFutureBookingsDialog.cancel.description',
                )}
              </Typography>

              <FormControlLabel
                value={false}
                control={<Radio />}
                label={t(
                  'employeesGroups:spaceGroups.employeesHaveActiveOrFutureBookingsDialog.doNotCancel.title',
                )}
              />
              <Typography variant="caption" color="textSecondary">
                {t(
                  'employeesGroups:spaceGroups.employeesHaveActiveOrFutureBookingsDialog.doNotCancel.description',
                )}
              </Typography>
            </RadioGroup>
          </FormControl>
        </Box>
      </Dialog>

      <CancelledBookingsSummaryDialog
        open={isCancelledBookingsSummaryDialogOpen}
        onCancel={handleCloseCancelledBookingsSummaryDialog}
        cancelledBookings={bookingsToManage}
      />
    </>
  );
};
